/* Copyright (c) 2023-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
    forwardRef
} from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { A11yModule } from '@angular/cdk/a11y';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { BidiModule } from '@angular/cdk/bidi';
import { FlexModule } from '@angular/flex-layout/flex';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgClass } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, interval, Observable, Subject, fromEvent, EMPTY } from 'rxjs';
import { takeUntil, filter, debounceTime, finalize, tap, catchError } from 'rxjs/operators';
import { isEmpty, isEqual, isNil } from 'lodash';
import { AccessibilityService } from 'crimson/lib/accessibility/accessibility.service';
import { ImageFormatService } from 'crimson/lib/image-format/image-format.service';
import { ImageProperties } from 'crimson/lib/image-format/image-format';
import { LocaleService } from 'crimson/lib/locale-service/locale.service';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { NetworkStatusService } from 'crimson/lib/network-status-service/network-status.service';
import { KeyboardEventArrowKeyValue, consumeKeydownUntilKeyup } from 'utils/lib/web-events/keyboard-event';
import { ResizeObserverUtils } from 'utils/lib/resize-observer/resize-observer-utils';
import { mapScrimRGBToFontColor, hexToRGB } from 'utils/lib/common/common-utils';
import { EllipsifiedTextWithTooltipDirective } from '../../directives/ellipsified-text-with-tooltip.directive';
import { NvFocusRippleDirective } from '../../directives/focus-ripple.directive';
import { HyperLinkUserActivationGuardDirective } from '../../directives/hyperlink-user-activation-guard.directive';
import { OffDeviceLinkDirective } from '../../directives/off-device-link.directive';
import { NvProgressCircularDirective } from '../../directives/progress-circular.directive';
import { SpatialNavigationGroupDirective } from '../../directives/spatial-navigation-container.directive';
import { FocusManager } from '../../services/focus-manager';
import { isHorizontalDir } from '../../services/spatial-navigation/spatial-navigation';
import { NavigationEvent } from '../../services/spatial-navigation/spatial-navigation-polyfill/index.d';
import { SpatialNavigationService } from '../../services/spatial-navigation/spatial-navigation.service';
import { NvHigProgressCircularComponent } from '../nv-hig-progress/nv-hig-progress-circular.component';
import {
    DefaultHigMarqueeFallbackImage,
    HigMarqueeActionClickEvent,
    HigMarqueeAppMetadata,
    HigMarqueeGradientConfig,
    HigMarqueeImageConfig,
    HigMarqueeOverlayConfig,
    HigMarqueeSwitchableImageConfig,
    HigMarqueeScrollEvent,
    HigMarqueeTile,
    HigMarqueeTileActionData,
    HigMarqueeTileType,
    HigMarqueeViewAppDetailsEvent,
    HigMarqueeImageFetcher
} from './nv-hig-marquee';

@Component({
    selector: 'nv-hig-marquee',
    templateUrl: './nv-hig-marquee.component.html',
    styleUrls: ['./nv-hig-marquee.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SpatialNavigationGroupDirective,
        NgClass,
        ExtendedModule,
        FlexModule,
        BidiModule,
        MatButtonModule,
        NvFocusRippleDirective,
        EllipsifiedTextWithTooltipDirective,
        forwardRef(() => OffDeviceLinkDirective),
        HyperLinkUserActivationGuardDirective,
        NvProgressCircularDirective,
        MatIconModule,
        A11yModule,
        NvHigProgressCircularComponent,
        TranslateModule
    ]
})
export class NvHigMarqueeComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('marqueeContainer') private marqueeContainer: ElementRef;
    @ViewChildren('marqueeTileContainerList') marqueeTileContainerList: QueryList<ElementRef<HTMLElement>>;
    @ViewChild('paginator') private paginator: ElementRef;
    @ViewChildren('paginatorList') paginatorList: QueryList<ElementRef<HTMLElement>>;

    private _tileData: HigMarqueeTile[];
    @Input() set tileData(tiles: HigMarqueeTile[]) {
        this._tileData = tiles;
        this.refreshMarquee(true);
    }
    get tileData(): HigMarqueeTile[] {
        return this._tileData;
    }

    private _autoScrollEnabled = true;
    @Input()
    set autoScrollEnabled(enabled: boolean) {
        this._autoScrollEnabled = enabled;
        if (enabled) {
            this.initAutoScroll();
        } else {
            this.pause();
        }
    }
    get autoScrollEnabled(): boolean {
        return this._autoScrollEnabled;
    }

    @Input() useMarqueeScrim = false;
    @Input() phoneMarqueeEnabled = false;
    @Input() mobileMode = false;
    @Input() tvMode = false;
    @Input() dismissRatio = 0;
    @Input() enablePaginatorNavigation = false;
    @Input() suppressContainerTabStop = false;
    @Input() paginatorAriaLabel = '';
    @Input() previousButtonAriaLabel = 'marquee.previous';
    @Input() nextButtonAriaLabel = 'marquee.next';
    @Input() gradientOverrides: Partial<HigMarqueeGradientConfig>;
    @Input() overlayOverrides: HigMarqueeOverlayConfig;
    @Input() enableAnimation = true;
    @Input() enableContentVisibilityOptimization = false;
    @Input() glassUi = false;

    /* Switchable Image Mode overrides the default rendering style with default and alternate styles
     * respectively, based on the breakpoint. */
    private _imageOverrides: Partial<HigMarqueeImageConfig>;
    @Input() set imageOverrides(overrides: Partial<HigMarqueeImageConfig>) {
        if (!isEqual(this._imageOverrides, overrides)) {
            this._imageOverrides = overrides;
            this.updateImageOverrides();
        }
    }

    get imageOverrides(): Partial<HigMarqueeImageConfig> {
        return this._imageOverrides;
    }

    @Output() actionClicked = new EventEmitter<HigMarqueeActionClickEvent>();
    @Output() viewAppDetails = new EventEmitter<HigMarqueeViewAppDetailsEvent>();
    @Output() imageLoadFailure = new EventEmitter<number>();
    @Output() marqueeScrolled = new EventEmitter<HigMarqueeScrollEvent>();
    @Output() paginatorRepositioned = new EventEmitter<number>();
    @Output() initialized = new EventEmitter<void>();
    @Output() beforeChangeSlide = new EventEmitter<number>();
    @Output() marqueeAutoScrollCancelled = new EventEmitter<void>();

    marqueeImageUrl: string;
    appLogoUrl: string;
    appTitle: string;
    appSubtitle: string;
    fontColor: string;
    scrimColor: string;
    scrimColorRight: string;
    marqueeData: HigMarqueeAppMetadata[];
    slideAccessor: number[] = [];

    position = 0;
    busyLoadingMarquee = true;
    errorVisible = false;
    isTileTypeFullBleedImage = false;
    isTileTypeFullBleedImageNotAtFirst = false;
    tileIsShowingFallbackImage = false;
    globalFallbackImage = DefaultHigMarqueeFallbackImage;
    globalFallbackImageType = HigMarqueeTileType.FullBleedImage;
    formatImages = true;
    backgroundOverhangEnabled = true;
    imageMaskDisabled = false;
    containerCustomBackground = false;
    contentOverlayType = HigMarqueeTileType.FullBleedImage;

    private isHandsetPortrait = false;
    private tileInFocus: HigMarqueeAppMetadata;
    private logger: Logger;
    private ismarketingTileReloaded = false;
    private marqueeIteratorSubscription: Subscription;
    private marqueeIterator: Observable<number>;
    private autoScrollInterval = 5000;
    private imageFetchSubsciption: Subscription;
    private destroy$: Subject<boolean>;
    private bottomEdgeGradientEnabled: boolean;
    private leftEdgeGradientEnabled: boolean;
    private leftMergeGradientEnabled: boolean;
    private backgroundGradientEnabled: boolean;
    private _hasMarqueeScrim: boolean;
    private switchableImageConfig: HigMarqueeSwitchableImageConfig;
    private _elementRef: ElementRef<HTMLElement>;

    // Dynamic tabindex that changes based on screen reader state
    tabIndexValue: number | null = null;

    get elementRef() {
        return this._elementRef;
    }

    get tileOption(): typeof HigMarqueeTileType {
        return HigMarqueeTileType;
    }

    get hasMarqueeScrim(): boolean {
        return this._hasMarqueeScrim;
    }

    set hasMarqueeScrim(state: boolean) {
        this._hasMarqueeScrim = state;
    }

    get hasFocus(): boolean {
        return this.marqueeContainer.nativeElement.contains(this.focusManager.currentFocusedElement);
    }

    get hasFocusable(): boolean {
        return !!this.focusManager.getInitialFocusable(this.marqueeContainer.nativeElement);
    }

    constructor(
        elementRef: ElementRef<HTMLElement>,
        loggingService: LoggingService,
        private imageFormatService: ImageFormatService,
        private networkStatusService: NetworkStatusService,
        private localeService: LocaleService,
        private breakpointObserver: BreakpointObserver,
        private spatialNavigationService: SpatialNavigationService,
        private focusManager: FocusManager,
        private changeDetector: ChangeDetectorRef,
        private translate: TranslateService,
        private ngZone: NgZone,
        private accessibilityService: AccessibilityService,
        @Optional() @Inject('MARQUEE_IMAGE_METADATA_FETCHER') private imageFetcher: HigMarqueeImageFetcher
    ) {
        this._elementRef = elementRef;
        this.logger = loggingService.getLogger('hig/MarqueeComponent');
        this.destroy$ = new Subject();
    }

    ngOnInit() {
        // Initialize dynamic tabindex with default behavior
        this.updateTabIndexValue(false);

        if (this.gradientOverrides) {
            this.bottomEdgeGradientEnabled = this.gradientOverrides.bottomEdge ?? this.bottomEdgeGradientEnabled;
            this.leftEdgeGradientEnabled = this.gradientOverrides.leftEdge ?? this.leftEdgeGradientEnabled;
            this.leftMergeGradientEnabled = this.gradientOverrides.leftMerge ?? this.leftMergeGradientEnabled;
            this.backgroundGradientEnabled = this.gradientOverrides.background ?? this.backgroundGradientEnabled;
            this.backgroundOverhangEnabled =
                this.gradientOverrides.backgroundOverhang ?? this.backgroundOverhangEnabled;
        }
        this.constructMarquee();
        if (this.switchableImageConfig) {
            this.breakpointObserver
                .observe(`(min-width: ${this.switchableImageConfig.breakpointPx}px)`)
                .pipe(takeUntil(this.destroy$))
                .subscribe(result => {
                    if (result.matches) {
                        this.overrideImageType(this.switchableImageConfig.alternate);
                        this.changeDetector.detectChanges();
                    }
                });
            this.breakpointObserver
                .observe(`(max-width: ${this.switchableImageConfig.breakpointPx - 1}px)`)
                .pipe(takeUntil(this.destroy$))
                .subscribe(result => {
                    if (result.matches) {
                        this.overrideImageType(this.switchableImageConfig.default);
                        this.changeDetector.detectChanges();
                    }
                });
        }
        // We need to explicitly check for network going online from offline in order to refresh the marquee
        // for the scenario where the layout refresh is not triggered due to hashes being equal
        this.networkStatusService.onlineStatusChanged
            .pipe(filter(isOnline => isOnline === true))
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.refreshMarquee();
                this.changeDetector.detectChanges();
            });

        if (this.phoneMarqueeEnabled) {
            this.breakpointObserver
                .observe(Breakpoints.HandsetPortrait)
                .pipe(takeUntil(this.destroy$))
                .subscribe(result => {
                    this.isHandsetPortrait = result.matches;
                    this.updateMarqueeImageUrl(this.tileInFocus.app);
                    this.notifyPaginatorRepositioned();
                    this.changeDetector.detectChanges();
                });
            ResizeObserverUtils.onResize(this.marqueeContainer.nativeElement)
                .pipe(debounceTime(200), takeUntil(this.destroy$))
                .subscribe(_ => {
                    this.notifyPaginatorRepositioned();
                });
        }
    }

    private updateImageOverrides(): void {
        this.globalFallbackImage = this.imageOverrides?.globalFallbackImageUrl ?? this.globalFallbackImage;
        this.globalFallbackImageType = this.imageOverrides?.globalFallbackImageType ?? this.globalFallbackImageType;
        this.formatImages = this.imageOverrides?.formatImages ?? this.formatImages;
        this.imageMaskDisabled = this.imageOverrides?.disableMask ?? this.imageMaskDisabled;
        this.containerCustomBackground = this.imageOverrides?.customBackground ?? this.containerCustomBackground;
        this.switchableImageConfig = this.imageOverrides?.switchableImageConfig;
        if (!isEmpty(this.marqueeData)) {
            const noTileData = isNil(this.tileData) || isEmpty(this.tileData);
            if (noTileData) {
                const appData = {
                    fallbackImage: this.globalFallbackImage,
                    type: this.globalFallbackImageType
                } as HigMarqueeTile;
                this.marqueeData = [
                    {
                        ...this.marqueeData[0],
                        app: appData
                    }
                ];
                const isFallbackImage = this.globalFallbackImageType !== HigMarqueeTileType.PaddedImage;
                this.updateImageTypeByIndex(0, appData.type, isFallbackImage);
            }
        }
    }

    private overrideImageType(imageType: HigMarqueeTileType) {
        this.marqueeData = this.marqueeData?.map(marqueeDataItem => {
            marqueeDataItem.imageType = imageType;
            return marqueeDataItem;
        });
        this.updateImageTypeByIndex(this.position, imageType, this.marqueeData[this.position].isShowingFallbackImage);
    }

    ngAfterViewInit(): void {
        if (this.spatialNavigationService.enabled && this.enablePaginatorNavigation) {
            fromEvent<NavigationEvent>(this.marqueeContainer?.nativeElement, 'navbeforefocus')
                .pipe(takeUntil(this.destroy$))
                .subscribe(this.onContainerNavBeforeFocus.bind(this));

            fromEvent<NavigationEvent>(this.marqueeContainer?.nativeElement, 'navnotarget')
                .pipe(
                    filter(e => e.target === this.marqueeContainer?.nativeElement && isHorizontalDir(e.detail.dir)),
                    takeUntil(this.destroy$)
                )
                .subscribe(this.navigateMarqueeSlide.bind(this));

            // Horizontal slide navigation from the action area / paginator. With roving
            // tabindex (CL 3556400) the polyfill has only one focusable dot so geometric
            // horizontal candidates can fail. Intercept ArrowLeft/ArrowRight in capture phase
            // (same idea as gfn-marquee / nvAppMarqueeKeyboardNav); teardown via takeUntil.
            fromEvent<KeyboardEvent>(this._elementRef.nativeElement, 'keydown', { capture: true })
                .pipe(takeUntil(this.destroy$))
                .subscribe(this.onMarqueeKeydown);
        }

        this.accessibilityService
            .onScreenReaderNavigating()
            .pipe(takeUntil(this.destroy$))
            .subscribe(screenReaderNavigating => {
                this.ngZone.run(() => {
                    this.updateTabIndexValue(screenReaderNavigating);
                });
            });

        this.initialized.emit();
    }

    /**
     * Updates the tabindex based on screen reader state and component configuration.
     * Null when screen reader is navigating or suppressContainerTabStop is true;
     * otherwise 0 when enablePaginatorNavigation is true, else null.
     */
    private updateTabIndexValue(screenReaderNavigating: boolean): void {
        this.tabIndexValue =
            screenReaderNavigating || this.suppressContainerTabStop ? null : this.enablePaginatorNavigation ? 0 : null;

        this.changeDetector.detectChanges();
    }

    onContainerNavBeforeFocus(e: NavigationEvent) {
        if (!this.hasFocus) {
            this.navigateIntoMarquee(e);
        } else {
            this.navigateWithinMarquee(e);
        }
    }

    private navigateIntoMarquee(e: NavigationEvent) {
        let candidate: HTMLElement;
        switch (e.detail.dir) {
            case 'up':
                candidate = this.paginatorList.get(this.position)?.nativeElement;
                break;
            case 'down':
            case 'left':
            case 'right':
                candidate = this.focusManager.getFirstFocusableChild(
                    this.marqueeTileContainerList.get(this.position).nativeElement
                );
                break;
        }

        if (!candidate) {
            candidate = this.paginatorList.get(this.position)?.nativeElement;
        }

        if (candidate) {
            e.preventDefault();
            this.focusManager.focusViaLastOrigin(candidate);
        }
    }

    private navigateWithinMarquee(e: NavigationEvent) {
        const hasActionFocus = this.marqueeTileContainerList
            .get(this.position)
            .nativeElement.contains(this.focusManager.currentFocusedElement);
        const targetPagerFocus = this.paginator.nativeElement.contains(e.target as HTMLElement);
        const targetActionFocus = this.marqueeTileContainerList
            .get(this.position)
            .nativeElement.contains(e.target as HTMLElement);

        if (hasActionFocus && targetPagerFocus && isHorizontalDir(e.detail.dir)) {
            this.navigateMarqueeSlide(e);
            e.preventDefault();
        } else if (e.target === this.paginator?.nativeElement) {
            e.preventDefault();
            this.focusManager.focusViaLastOrigin(this.paginatorList.get(this.position)?.nativeElement);
        } else if (targetActionFocus && isHorizontalDir(e.detail.dir)) {
            // Prevent focus form paginator to button in the horizontal direction
            // LTR first paginator element going left
            // RTL last paginator element going right
            e.preventDefault();
            // search focus to propagate if there are candidates outside the marquee
            const candidate = this.spatialNavigationService.findNextTarget(e.target as HTMLElement, e.detail.dir);
            if (candidate && !this.marqueeContainer.nativeElement.contains(candidate)) {
                this.focusManager.navigateTo(candidate as HTMLElement, e.detail.dir);
            }
        } else {
            // no-op
            // we will let polifill spatial nav take care of the equivalent to
            // this.focusManager.focusViaLastOrigin(e.target as HTMLElement);
        }
    }

    private onMarqueeKeydown = (e: KeyboardEvent) => {
        if (e.key !== KeyboardEventArrowKeyValue.ArrowLeft && e.key !== KeyboardEventArrowKeyValue.ArrowRight) {
            return;
        }
        const activeEl = document.activeElement as HTMLElement;
        const focusOnAction = this.marqueeTileContainerList.get(this.position)?.nativeElement.contains(activeEl);
        const focusOnPaginator = this.paginator?.nativeElement.contains(activeEl);
        if (!focusOnAction && !focusOnPaginator) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        const goNext = this.localeService.isRTLLayoutEnabled
            ? e.key === KeyboardEventArrowKeyValue.ArrowLeft
            : e.key === KeyboardEventArrowKeyValue.ArrowRight;
        if (goNext) {
            this.nextSlide();
        } else {
            this.prevSlide();
        }
        if (focusOnPaginator) {
            const activeDot = this.paginatorList.get(this.position)?.nativeElement;
            if (activeDot) {
                this.focusManager.focusViaLastOrigin(activeDot);
            }
        }
        this.changeDetector.detectChanges();
    };

    private navigateMarqueeSlide(e: NavigationEvent) {
        let slideChanged = false;
        if (this.localeService.isRTLLayoutEnabled ? e.detail.dir === 'left' : e.detail.dir === 'right') {
            slideChanged = this.nextSlide();
        } else {
            slideChanged = this.prevSlide();
        }

        if (slideChanged) {
            e.preventDefault();
            this.changeDetector.detectChanges();
        }
    }

    private notifyPaginatorRepositioned() {
        // wait for elements updated by orientation change
        setTimeout(() => {
            const containerTop = this.marqueeContainer?.nativeElement.getBoundingClientRect().top;
            let paginatorBottom: number;
            let marginBottom: number;

            if (this.paginator?.nativeElement !== undefined) {
                paginatorBottom = this.paginator?.nativeElement.getBoundingClientRect().bottom;
                marginBottom = parseInt(
                    getComputedStyle(this.paginator?.nativeElement).getPropertyValue('margin-bottom'),
                    10
                );
            } else {
                paginatorBottom = this.marqueeTileContainerList
                    .get(this.position)
                    .nativeElement.getBoundingClientRect().bottom;
                marginBottom = 32;
            }

            if (paginatorBottom !== undefined && containerTop !== undefined) {
                this.paginatorRepositioned.next(Math.floor(paginatorBottom + marginBottom - containerTop));
            }
        });
    }

    private constructMarquee() {
        this.marqueeData = [];
        this.slideAccessor = [];
        const noTileData = isNil(this.tileData) || isEmpty(this.tileData);
        if (noTileData) {
            const appData = {
                heroImage: this.globalFallbackImage,
                marqueeHeroImage: this.globalFallbackImage,
                type: this.globalFallbackImageType
            } as HigMarqueeTile;
            // this is the default type if data is passed empty, and will be handled with a fallback image
            this.marqueeData.push({
                isVisible: false,
                app: appData,
                gradients: this.getGradientConfig(appData),
                contentOverlayType: this.getContentOverlayType(appData),
                imageType: appData.type,
                isShowingFallbackImage: this.globalFallbackImageType !== HigMarqueeTileType.PaddedImage
            });
            this.updateImageTypeByIndex(0, appData.type, this.marqueeData[0].isShowingFallbackImage);
        } else {
            this.tileData.forEach((appData, index) => {
                this.marqueeData.push({
                    isVisible: false,
                    app: appData,
                    gradients: this.getGradientConfig(appData),
                    contentOverlayType: this.getContentOverlayType(appData),
                    imageType: appData.type,
                    isShowingFallbackImage: noTileData
                });
                this.slideAccessor.push(index);
            });
        }

        this.logger.info('Marquee Initialization');
        this.errorVisible = false;
        this.marqueeInitialization();
        this.busyLoadingMarquee = false;
        this.ismarketingTileReloaded = true;
    }

    private getGradientConfig(appData: HigMarqueeTile): HigMarqueeGradientConfig {
        return {
            bottomEdge: this.bottomEdgeGradientEnabled ?? appData.type === HigMarqueeTileType.FullBleedImage,
            leftEdge: this.leftEdgeGradientEnabled ?? appData.type === HigMarqueeTileType.PaddedImage,
            leftMerge: this.leftMergeGradientEnabled ?? false,
            background: this.backgroundGradientEnabled ?? appData.type === HigMarqueeTileType.PaddedImage,
            backgroundOverhang: this.backgroundOverhangEnabled ?? true
        };
    }

    private getContentOverlayType(appData: HigMarqueeTile): HigMarqueeTileType {
        if (this.overlayOverrides) {
            return this.overlayOverrides.contentOverlayType;
        } else {
            return appData.type;
        }
    }

    private updateMetaData(marqueeData: HigMarqueeAppMetadata) {
        const app = marqueeData.app;
        this.appLogoUrl = app.logoImage
            ? this.formatImages
                ? this.imageFormatService.formatImageServiceUrl(app.logoImage)
                : app.logoImage
            : null;
        this.updateMarqueeImageUrl(app);
    }

    private updateMarqueeImageUrl(app: HigMarqueeTile) {
        let dimens: ImageProperties;
        let sourceUrl: string;
        if (this.phoneMarqueeEnabled && this.isHandsetPortrait) {
            dimens = { w: 960 };
            sourceUrl = app.heroImage;
        } else {
            dimens = { w: 1920 };
            sourceUrl = app.marqueeHeroImage;
        }

        if (sourceUrl) {
            const imageUrl = this.formatImages
                ? this.imageFormatService.formatImageServiceUrl(sourceUrl, dimens)
                : sourceUrl;
            if (this.imageFetcher) {
                this.fetchImageAndColors(app, imageUrl);
            } else {
                if (this.isTileTypeFullBleedImage) {
                    this.scrimColor = '0, 0, 0';
                    this.hasMarqueeScrim = true;
                }
                this.marqueeImageUrl = imageUrl;
                this.changeDetector.detectChanges();
            }
        } else {
            this.marqueeImageUrl = null;
            this.changeDetector.detectChanges();
        }
    }

    /**
     * Picks the left/right scrim hex colors for the l/r gradient from the image's sampled edge colors.
     * Phone portrait uses the bottom color for both stops; RTL swaps left/right (the CSS gradient stays 90deg).
     */
    private resolveScrimHexColors(colors: { left?: string; right?: string; bottom?: string }): {
        left?: string;
        right?: string;
    } {
        if (this.phoneMarqueeEnabled && this.isHandsetPortrait && colors.bottom) {
            return { left: colors.bottom, right: colors.bottom };
        }
        if (this.localeService.isRTLLayoutEnabled) {
            return { left: colors.right || colors.left, right: colors.left || colors.right };
        }
        return { left: colors.left, right: colors.right };
    }

    private fetchImageAndColors(app: HigMarqueeTile, imageUrl: string) {
        this.logger.info('image fetch started ' + imageUrl);
        let color: { r: number; g: number; b: number };
        let colorRight: { r: number; g: number; b: number }; //Needed for the l/r gradient.
        let image = null;

        this.imageFetchSubsciption?.unsubscribe();

        this.imageFetchSubsciption = this.imageFetcher
            .fetchImage(imageUrl)
            .pipe(
                tap(result => {
                    image = result.imageUrl;
                    if (result.metadata?.colors) {
                        const { left: hexColor, right: hexColorRight } = this.resolveScrimHexColors(
                            result.metadata.colors
                        );

                        if (hexColor) {
                            color = hexToRGB(hexColor);
                        }
                        if (hexColorRight) {
                            colorRight = hexToRGB(hexColorRight);
                        }
                    }
                }),
                catchError(err => {
                    this.logger.error('Failed to fetch image:', err);
                    return EMPTY;
                }),
                finalize(() => {
                    if (app == this.tileInFocus.app) {
                        this.tileInFocus.isShowingFallbackImage = false;
                        this.updateImageTypeByIndex(
                            this.position,
                            this.tileInFocus?.imageType,
                            this.tileInFocus.isShowingFallbackImage
                        );
                        this.marqueeImageUrl = image;
                        color = color || { r: 0, g: 0, b: 0 };
                        this.scrimColor = `${color.r}, ${color.g}, ${color.b}`;
                        this.scrimColorRight = colorRight
                            ? `${colorRight.r}, ${colorRight.g}, ${colorRight.b}`
                            : this.scrimColor;
                        this.fontColor = mapScrimRGBToFontColor(color);
                        this.hasMarqueeScrim = true;
                        this.changeDetector.detectChanges();
                        this.logger.info('image loaded ' + this.marqueeImageUrl);
                    }
                })
            )
            .subscribe();
    }

    private marqueeInitialization() {
        this.position = 0;
        if (this.marqueeData[this.position]) {
            this.marqueeData[this.position].isVisible = true;
        }
        this.setSlideMetaData(0);
    }

    private setSlideMetaData(index) {
        if (!this.marqueeData[index]) {
            return;
        }
        this.tileInFocus = this.marqueeData[index];
        this.updateImageTypeByIndex(index, this.tileInFocus?.imageType, this.tileInFocus.isShowingFallbackImage);
        this.resetData();
        this.updateMetaData(this.tileInFocus);
        // Display all supported app store names
        if (!this.isTileTypeFullBleedImage) {
            this.appSubtitle = this.tileInFocus.app.subtitle;
        }
    }

    private updateImageTypeByIndex(index: number, imageType: HigMarqueeTileType, isFallbackImage: boolean): void {
        this.tileIsShowingFallbackImage = isFallbackImage;
        this.isTileTypeFullBleedImage = imageType === HigMarqueeTileType.FullBleedImage;
        this.isTileTypeFullBleedImageNotAtFirst = this.isTileTypeFullBleedImage && index > 0;
    }

    /**
     * Nullifying component variables
     */
    private resetData() {
        this.marqueeImageUrl = null;
        this.appLogoUrl = null;
        this.appTitle = null;
        this.hasMarqueeScrim = false;
        this.scrimColor = null;
        this.scrimColorRight = null;
        this.fontColor = 'white'; // Default font color should be white
        this.appSubtitle = null;
    }

    private initAutoScroll() {
        if (this.autoScrollEnabled) {
            this.marqueeIterator = interval(this.autoScrollInterval);
            this.marqueeIteratorSubscription?.unsubscribe();
            this.marqueeIteratorSubscription = this.marqueeIterator
                .pipe(
                    filter(
                        () =>
                            document?.hasFocus() &&
                            this.dismissRatio == 0 &&
                            this.paginator?.nativeElement.getBoundingClientRect().bottom > 0
                    )
                )
                .subscribe(() => this.autoScrollNext());
        }
    }

    goToSlide(slideNumber: number) {
        this.cancelAutoSlide();
        if (this.position === slideNumber) {
            return;
        }

        this.changeSlide(slideNumber);
        this.autoScrollEnabled = false;
        this.marqueeScrolled.emit({ position: this.position, autoScrolled: this.autoScrollEnabled });
    }

    private changeSlide(slideNumber: number) {
        this.beforeChangeSlide.emit(slideNumber);
        // record focus before change
        const hadFocus = this.marqueeContainer.nativeElement.contains(this.focusManager.currentFocusedElement);
        const hadActionFocus = this.marqueeTileContainerList
            .get(this.position)
            .nativeElement.contains(this.focusManager.currentFocusedElement);
        // Change slide
        this.marqueeData.map((app, index) => {
            index === slideNumber ? (app.isVisible = true) : (app.isVisible = false);
        });
        const prevPosition = this.position;
        this.position = slideNumber;
        this.setSlideMetaData(this.position);

        // Restore focus after change
        if (this.spatialNavigationService.enabled && this.enablePaginatorNavigation && hadFocus) {
            let offset = this.position - prevPosition;
            offset *= this.localeService.isRTLLayoutEnabled ? -1 : 1;
            const dir = offset > 0 ? KeyboardEventArrowKeyValue.ArrowRight : KeyboardEventArrowKeyValue.ArrowLeft;
            const consumeKeydownUntilKeyupSub = consumeKeydownUntilKeyup(window, dir)
                .pipe(takeUntil(this.destroy$))
                .subscribe();

            setTimeout(() => {
                const actionCandidate = this.focusManager.getFirstFocusableChild(
                    this.marqueeTileContainerList.get(this.position).nativeElement
                );
                const candidate =
                    hadActionFocus && actionCandidate
                        ? actionCandidate
                        : this.paginatorList.get(this.position)?.nativeElement;
                this.focusManager.focusViaLastOrigin(candidate);
                if (!consumeKeydownUntilKeyupSub.closed) {
                    consumeKeydownUntilKeyupSub.unsubscribe();
                }
            });
        }
    }

    prevSlide(): boolean {
        this.cancelAutoSlide();
        if (this.position > 0) {
            this.goToSlide(this.position - 1);
            return true;
        }
        return false;
    }

    nextSlide(): boolean {
        this.cancelAutoSlide();
        if (this.position < this.marqueeData.length - 1) {
            this.goToSlide(this.position + 1);
            return true;
        }
        return false;
    }

    private autoScrollNext() {
        this.marqueeScrolled.emit({ position: this.position, autoScrolled: this.autoScrollEnabled });
        this.changeSlide((this.position + 1) % this.marqueeData.length);
        this.changeDetector.detectChanges();
    }

    private refreshMarquee(contentUpdated?: boolean) {
        if (this.errorVisible || contentUpdated) {
            this.logger.info('Marquee data updated, constructing marquee...');
            this.constructMarquee();
        }
    }

    private pause() {
        this.marqueeIteratorSubscription?.unsubscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.imageFetchSubsciption?.unsubscribe();
        this.marqueeIteratorSubscription?.unsubscribe();
    }

    swipeLeft() {
        if (this.localeService.isRTLLayoutEnabled) {
            this.prevSlide();
        } else {
            this.nextSlide();
        }
    }

    swipeRight() {
        if (this.localeService.isRTLLayoutEnabled) {
            this.nextSlide();
        } else {
            this.prevSlide();
        }
    }

    marqueeImageLoadComplete() {
        if (this.position === 0 && this.ismarketingTileReloaded === true) {
            this.ismarketingTileReloaded = false;
        }
    }

    fallbackToPlaceholderImage(): void {
        const tile = this.marqueeData[this.position];
        const app = tile.app;
        this.logger.warn('Error loading image for ' + app.title + ' using fallback.');
        const noTileData = isNil(this.tileData) || isEmpty(this.tileData);
        if (noTileData) {
            tile.isShowingFallbackImage = tile.imageType !== HigMarqueeTileType.PaddedImage;
        } else {
            tile.isShowingFallbackImage = true;
            this.updateImageTypeByIndex(this.position, tile.imageType, tile.isShowingFallbackImage);
        }
        this.marqueeImageUrl = app.fallbackImage ? app.fallbackImage : this.globalFallbackImage;
        this.imageLoadFailure.next(this.position);
    }

    fallbackToAppLogo(): void {
        this.logger.warn(
            'Error loading app logo for ' + this.marqueeData[this.position].app.title + ' using fallback.'
        );
        this.appTitle = this.marqueeData[this.position].app.title;
    }

    getActionAriaLabel(tile: HigMarqueeAppMetadata, actionLabel?: string): string {
        const label = actionLabel ?? this.translate.instant('common.viewDetails');
        const title = tile.app?.title || this.appTitle;
        return title ? `${title}, ${label}` : label;
    }

    onViewAppDetails(event: MouseEvent, app: HigMarqueeTile): void {
        this.cancelAutoSlide();
        this.logger.info('view app details', event);
        this.viewAppDetails.emit({
            app,
            ...event
        });
    }

    onActionClicked(event: MouseEvent, action: HigMarqueeTileActionData, position: number, id: string) {
        this.cancelAutoSlide();
        this.logger.info('action clicked', event, action);
        this.actionClicked.emit({
            action,
            position,
            id,
            ...event
        });
    }

    setInitialFocus(): void {
        this.focusManager.focusChild(this.marqueeContainer.nativeElement, 'first');
    }

    cancelAutoSlide(): void {
        this.autoScrollEnabled = false;
        this.marqueeAutoScrollCancelled.emit();
    }
}
